import { ILibraryFolder } from "@/api";
import { ILibraryDragItem } from "@/components/shared/document-picker/DocumentPicker.types";

import {
    isForeignSharedSubject,
    isProtectedRootFolder,
} from "./folderTreeUtils";

export function getDescendantFolderIds(
    folders: ILibraryFolder[],
    folderId: number
): Set<number> {
    const descendants = new Set<number>();
    const queue = [folderId];
    while (queue.length > 0) {
        const current = queue.pop()!;
        for (const f of folders) {
            if (f.parent_id === current && !descendants.has(f.id)) {
                descendants.add(f.id);
                queue.push(f.id);
            }
        }
    }
    return descendants;
}

export function isValidDropForTarget(
    folders: ILibraryFolder[],
    items: ILibraryDragItem[],
    targetFolderId: number | null,
    targetIsForeignShared = false
): boolean {
    if (targetIsForeignShared) return false;
    const targetFolder = folders.find((folder) => folder.id === targetFolderId);
    if (targetFolder && isForeignSharedSubject(targetFolder)) return false;

    const subjects = items.filter((item) => {
        if (item.isForeignShared) return false;
        if (item.type !== "folder") return true;
        const subject = folders.find((f) => f.id === item.numericId);
        return !subject || !isProtectedRootFolder(subject);
    });
    if (subjects.length === 0) return false;

    if (targetFolderId === null) {
        // Root drop — check if all items are already at root
        const allAlreadyAtRoot = subjects.every((item) => {
            if (item.type === "folder") return item.parentId === null;
            return item.libraryFolderId === null;
        });
        return !allAlreadyAtRoot;
    }

    for (const item of subjects) {
        if (item.type !== "folder") continue;
        // A folder can't move into itself, into its own descendant (a cycle),
        // or across the KB boundary.
        if (item.numericId === targetFolderId) {
            return false;
        }
        if (
            getDescendantFolderIds(folders, item.numericId).has(targetFolderId)
        ) {
            return false;
        }
        if (
            targetFolder &&
            item.isKnowledgeBase !== targetFolder.is_knowledge_base
        ) {
            return false;
        }
    }

    const allAlreadyInTarget = subjects.every((item) => {
        if (item.type === "folder") {
            return item.parentId === targetFolderId;
        }
        return item.libraryFolderId === targetFolderId;
    });
    if (allAlreadyInTarget) return false;

    return true;
}
